import React from 'react';
import {
  Box,
  Card,
  Typography,
  useTheme,
  CircularProgress,
  Avatar,
  Chip,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { glassStyle } from '../theme/luxuryTheme';
import { useEmployeePerformance } from '../hooks/useAnalytics';
import { formatCurrency } from '../utils/formatters';

interface EmployeePerformanceProps {
  date?: string;
  storeId?: string;
}

const EmployeePerformance: React.FC<EmployeePerformanceProps> = ({ date, storeId }) => {
  const theme = useTheme();
  const { data, isLoading, error } = useEmployeePerformance(date, storeId);

  if (isLoading) {
    return (
      <Card sx={{ ...glassStyle, p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: theme.palette.primary.main }} />
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card sx={{ ...glassStyle, p: 4 }}>
        <Typography color="error">Failed to load employee performance data</Typography>
      </Card>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card sx={{ ...glassStyle, p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Employee Performance
          </Typography>
          <Chip
            label="Top Performers"
            size="small"
            sx={{
              backgroundColor: 'rgba(175, 35, 38, 0.2)',
              color: theme.palette.primary.main,
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {(data.topPerformers || data.employees || []).slice(0, 5).map((employee: any, index: number) => (
            <Box
              key={employee.id || employee.employeeId || index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 2,
                borderRadius: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.03)',
                border: index === 0 ? `1px solid ${theme.palette.primary.main}40` : 'none',
                position: 'relative',
              }}
            >
              {index === 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -10,
                    left: 20,
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                  }}
                >
                  TOP PERFORMER
                </Box>
              )}
              
              <Avatar
                sx={{
                  bgcolor: index === 0 ? theme.palette.primary.main : theme.palette.grey[700],
                  mr: 2,
                }}
              >
                {(employee.name || employee.employeeName) ? (employee.name || employee.employeeName).charAt(0).toUpperCase() : <PersonIcon />}
              </Avatar>
              
              <Box sx={{ flex: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {employee.name || employee.employeeName || `Employee ${employee.employeeId}`}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {employee.role || 'Team Member'}
                </Typography>
              </Box>
              
              <Box sx={{ textAlign: 'right' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                    {formatCurrency(employee.salesTotal || employee.totalSales || 0)}
                  </Typography>
                  <TrendingUpIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {employee.orderCount || 0} orders • {formatCurrency(employee.averageOrderValue || 0)} avg
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {data.employees && data.employees.length > 5 && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Showing top 5 of {data.employees.length} employees
            </Typography>
          </Box>
        )}
      </Card>
    </motion.div>
  );
};

export default EmployeePerformance;